import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@/hooks/use-toast';
import { fetchLiveComments, getOrderByCommentForComment, getOrderByComments } from '@/api/fbapi';
import { FBLive, FBPost, OrderByComment } from '@/types/fb-type';
import { FacebookComment } from '@/types/fb-type';
import { WsEvent } from '@/types/ws-type';
import { getPageId } from '@/services/utl-service';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { getPrimaryActiveLive } from '../lib/live-active-store';
import { isFreshLive, objectIdFromFbId } from '../lib/live-status';

function buildSelectionKeys(...ids: Array<string | null | undefined>) {
  const keys = new Set<string>();
  for (const id of ids) {
    if (!id) continue;
    keys.add(id);
    keys.add(objectIdFromFbId(id));
  }
  return keys;
}

function resolveCommentSourceId(comment: FacebookComment) {
  if (comment.postId) return comment.postId;
  if (comment.id.includes('_')) return comment.id.split('_')[0];
  return null;
}

function mergeCommentLists(prev: FacebookComment[], incoming: FacebookComment[]) {
  const byId = new Map<string, FacebookComment>();
  for (const comment of prev) byId.set(comment.id, comment);
  for (const comment of incoming) {
    const existing = byId.get(comment.id);
    byId.set(comment.id, existing ? { ...existing, ...comment } : comment);
  }
  return Array.from(byId.values()).sort(
    (a, b) => new Date(b.createdTime!).getTime() - new Date(a.createdTime!).getTime(),
  );
}

type UseLiveCommentFeedOptions = {
  fbLives: FBLive[];
  fetchLiveVideos: () => Promise<void>;
  upsertActiveLive: (input: { postId: string; title?: string }) => void;
  showingComment: boolean;
  onOpenPanel?: () => void;
};

export function useLiveCommentFeed({
  fbLives,
  fetchLiveVideos,
  upsertActiveLive,
  showingComment,
  onOpenPanel,
}: UseLiveCommentFeedOptions) {
  const commentTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [comments, setComments] = useState<FacebookComment[]>([]);
  const [orderByComments, setOrderByComments] = useState<OrderByComment[]>([]);
  const [postId, setPostId] = useState<string | null>(null);
  const lastFetchedAtRef = useRef<string | null>(null);
  const postIdRef = useRef<string | null>(null);
  const historyLoadedKeyRef = useRef<string | null>(null);
  const historyLoadingRef = useRef(false);
  const selectionKeysRef = useRef<Set<string>>(new Set());
  const followLiveFeedRef = useRef(true);
  const [isPolling, setIsPolling] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);
  const [fetchCommentState, setFetchCommentState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  useEffect(() => {
    lastFetchedAtRef.current = lastFetchedAt;
  }, [lastFetchedAt]);
  useEffect(() => {
    postIdRef.current = postId;
  }, [postId]);

  const isFBLive = (item: FBPost | FBLive): item is FBLive =>
    (item as FBLive).postId !== undefined || (item as FBLive).status !== undefined;

  const commentMatchesSelection = useCallback((comment: FacebookComment) => {
    const sourceId = resolveCommentSourceId(comment);
    if (!sourceId) return false;
    if (selectionKeysRef.current.size === 0) return followLiveFeedRef.current;
    const key = objectIdFromFbId(sourceId);
    return selectionKeysRef.current.has(sourceId) || selectionKeysRef.current.has(key);
  }, []);

  const ensureCommentHistory = useCallback(
    async (sourceId: string, opts?: { clearExisting?: boolean }) => {
      const key = objectIdFromFbId(sourceId);
      if (!sourceId || historyLoadedKeyRef.current === key || historyLoadingRef.current) return;

      historyLoadingRef.current = true;
      setFetchCommentState('loading');
      setPostId(sourceId);
      postIdRef.current = sourceId;
      if (opts?.clearExisting && historyLoadedKeyRef.current && historyLoadedKeyRef.current !== key) {
        setComments([]);
      }

      try {
        const res = await fetchLiveComments(sourceId, null);
        if (res.success) {
          historyLoadedKeyRef.current = key;
          setLastFetchedAt(res.data.lastFetchedAt);
          lastFetchedAtRef.current = res.data.lastFetchedAt;
          setComments((prev) => mergeCommentLists(prev, res.data.comments ?? []));
          setFetchCommentState('success');
        } else {
          setFetchCommentState('error');
          toast({
            title: 'Error',
            description: res.message || 'Error getting comments',
            variant: 'destructive',
          });
        }
      } catch (err) {
        console.error(err);
        setFetchCommentState('error');
      } finally {
        historyLoadingRef.current = false;
      }
    },
    [],
  );

  const bindToSource = useCallback(
    async (
      sourceId: string,
      opts?: {
        openPanel?: boolean;
        clearExisting?: boolean;
        followLive?: boolean;
        selectionIds?: Array<string | null | undefined>;
      },
    ) => {
      if (opts?.followLive !== undefined) followLiveFeedRef.current = opts.followLive;
      selectionKeysRef.current = buildSelectionKeys(sourceId, ...(opts?.selectionIds ?? []));
      if (opts?.openPanel) onOpenPanel?.();
      await ensureCommentHistory(sourceId, { clearExisting: opts?.clearExisting });
    },
    [ensureCommentHistory, onOpenPanel],
  );

  const handleSelectPost = useCallback(
    async (post: FBPost | FBLive) => {
      const live = isFBLive(post);
      const livePostId = live ? post.postId : null;
      const fetchId = livePostId || post.id;
      const key = objectIdFromFbId(fetchId);
      const sameSource = historyLoadedKeyRef.current === key;
      followLiveFeedRef.current = live;
      selectionKeysRef.current = buildSelectionKeys(post.id, livePostId, fetchId);
      if (sameSource) {
        setPostId(fetchId);
        postIdRef.current = fetchId;
        onOpenPanel?.();
        return;
      }
      await bindToSource(fetchId, { openPanel: true, clearExisting: true, followLive: live });
    },
    [bindToSource, onOpenPanel],
  );

  const tryBindCurrentLive = useCallback(async () => {
    if (!followLiveFeedRef.current || postIdRef.current) return;
    const remembered = getPrimaryActiveLive();
    if (remembered) {
      await bindToSource(remembered.postId || remembered.id, {
        selectionIds: [remembered.id, remembered.postId],
      });
      return;
    }
    await fetchLiveVideos();
  }, [bindToSource, fetchLiveVideos]);

  const openCommentPanel = useCallback(() => {
    const id = postIdRef.current;
    if (id) void ensureCommentHistory(id);
    else if (followLiveFeedRef.current) void tryBindCurrentLive();
  }, [ensureCommentHistory, tryBindCurrentLive]);

  useEffect(() => {
    if (!showingComment || !followLiveFeedRef.current) return;
    if (postIdRef.current || historyLoadedKeyRef.current) return;
    const liveNow = fbLives.find((live) => isFreshLive(live));
    if (!liveNow) return;
    void bindToSource(liveNow.postId || liveNow.id, {
      selectionIds: [liveNow.id, liveNow.postId],
    });
    upsertActiveLive({ postId: liveNow.postId || liveNow.id, title: liveNow.title || 'Live now' });
  }, [fbLives, showingComment, bindToSource, upsertActiveLive]);

  const loadMoreComment = useCallback(async () => {
    try {
      if (!postId) return;
      setIsPolling(true);
      const res = await fetchLiveComments(postId, lastFetchedAtRef.current);
      if (res.success && res.data.comments.length > 0) {
        lastFetchedAtRef.current = res.data.lastFetchedAt;
        setLastFetchedAt(res.data.lastFetchedAt);
        setComments((prev) => mergeCommentLists(prev, res.data.comments));
        const resp = await getOrderByComments(postId);
        if (resp.success) setOrderByComments(resp.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsPolling(false);
    }
  }, [postId]);

  const rebindLiveTab = useCallback(async () => {
    await fetchLiveVideos();
    const active = getPrimaryActiveLive();
    if (!active) return;
    const key = objectIdFromFbId(active.postId || active.id);
    if (historyLoadedKeyRef.current === key) {
      followLiveFeedRef.current = true;
      selectionKeysRef.current = buildSelectionKeys(active.id, active.postId);
      return;
    }
    await bindToSource(active.postId || active.id, {
      followLive: true,
      selectionIds: [active.id, active.postId],
    });
  }, [bindToSource, fetchLiveVideos]);

  const fetchOrderByComments = useCallback(async () => {
    if (!postId) return;
    const resp = await getOrderByComments(postId);
    if (resp.success) setOrderByComments(resp.data);
  }, [postId]);

  const handleNewCommentArrival = useCallback(
    async (newComment: FacebookComment) => {
      if (commentTimerRef.current) clearTimeout(commentTimerRef.current);
      const resp = await getOrderByCommentForComment(newComment.id);
      if (resp.success && resp.data != null) {
        setOrderByComments((prev) => {
          const existingIndex = prev.findIndex((order) => order.commentId === resp.data.commentId);
          if (existingIndex !== -1) {
            const updated = [...prev];
            updated[existingIndex] = resp.data;
            return updated;
          }
          return [...prev, resp.data];
        });
      }
      commentTimerRef.current = setTimeout(() => {
        void fetchOrderByComments();
        commentTimerRef.current = null;
      }, 1500);
    },
    [fetchOrderByComments],
  );

  const handleWsMessage = useCallback(
    (stompMsg: { body: string }) => {
      try {
        const event: WsEvent = JSON.parse(stompMsg.body);
        if (event.type !== 'NEW_COMMENT') return;

        const comment = event.payload as FacebookComment;
        const sourceId = resolveCommentSourceId(comment);

        if (followLiveFeedRef.current && selectionKeysRef.current.size === 0 && sourceId) {
          selectionKeysRef.current = buildSelectionKeys(sourceId);
          upsertActiveLive({ postId: sourceId, title: 'Live now' });
        }

        if (!commentMatchesSelection(comment)) return;

        setComments((prev) => {
          if (prev.some((c) => c.id === comment.id)) return prev;
          comment.isNew = true;
          return mergeCommentLists(prev, [comment]);
        });

        if (sourceId && followLiveFeedRef.current) {
          void ensureCommentHistory(sourceId);
        }

        void handleNewCommentArrival(comment);
        if (!showingComment) {
          toast({
            title: 'New comment',
            content: event.payload.message,
            variant: 'success',
          });
        }
      } catch (e) {
        console.warn('WS parse error', e, stompMsg.body);
      }
    },
    [
      showingComment,
      ensureCommentHistory,
      commentMatchesSelection,
      handleNewCommentArrival,
      upsertActiveLive,
    ],
  );

  const pageId = getPageId();
  useWebSocketChannel('/topic/feed/' + pageId, handleWsMessage);

  const handleWsNewOrderByComment = useCallback((stompMsg: { body: string }) => {
    try {
      const event: WsEvent = JSON.parse(stompMsg.body);
      if (event.type !== 'NEW_ORDER_BY_COMMENT') return;
      const newOrder = event.payload as OrderByComment;
      if (newOrder.customerBlocked) {
        toast({
          title: 'Blocked customer tried to order',
          description: `${newOrder.customerName ?? 'Customer'}: ${(newOrder.errorMsg ?? '').replace('BLOCKED CUSTOMER: ', '')}`,
          variant: 'destructive',
        });
      }
      setOrderByComments((prev) => {
        const index = prev.findIndex((o) => o.commentId === newOrder.commentId);
        if (index !== -1) {
          const updated = [...prev];
          updated[index] = newOrder;
          return updated;
        }
        return [...prev, newOrder];
      });
    } catch (e) {
      console.error('Error parsing OrderByComment WS message', e);
    }
  }, []);

  useWebSocketChannel('/topic/order-by-comments', handleWsNewOrderByComment);

  useEffect(() => {
    if (!postId) {
      setOrderByComments([]);
      return;
    }
    void fetchOrderByComments();
  }, [postId, fetchOrderByComments]);

  useEffect(() => {
    return () => {
      if (commentTimerRef.current) clearTimeout(commentTimerRef.current);
    };
  }, []);

  const enrichedComments = useMemo(() => {
    if (orderByComments.length === 0) return comments;
    const orderMap = new Map(orderByComments.map((item) => [item.commentId, item]));
    return comments.map((comment) =>
      orderMap.has(comment.id) ? { ...comment, orderByComment: orderMap.get(comment.id) } : comment,
    );
  }, [comments, orderByComments]);

  return {
    comments: enrichedComments,
    fetchCommentState,
    handleSelectPost,
    isPolling,
    loadMoreComment,
    openCommentPanel,
    rebindLiveTab,
  };
}
